import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import CommentsLabel from '@/components/CommentsLabel'
import CommentsSidebar from '@/components/CommentsSidebar'
import { ActionButtonContainer } from '@/components/DecisionPanelCards/Shared'
import {
  displayFailReasons,
  displaySuccessReasons,
} from '@/components/DecisionPanelCards/utils'
import DocumentRequests from '@/components/DocumentRequests'
import ExpandableCard from '@/components/ExpandableCard'
import { ButtonsContainer } from '@/components/IdentityCard/IdentityCard'
import OutcomeStatusPill from '@/components/OutcomeStatusPill'
import ProsCons from '@/components/ProsCons/ProsCons'
import VerifyOrganisation from '@/components/VerifyOrganisation/index'
import { OutcomeSectionName } from '@/lib/apollo/model/outcome'
import { SectionName } from '@/lib/apollo/query/get-document-requests'
import type { Application } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'
import { isCorporateTenant, isRefCompleted } from '@/utils/subjectHelpers'

import OrganisationalDetailsCard from './OrganisationalDetails'

interface Props {
  application: Application
  subjectTouchpoints: Touchpoint[]
  isLoadingTouchpoints: boolean
  fetchSubjectTouchpoints: () => Promise<void>
}

const Body = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 20px;
  transition: height 0.25s ease-in;
`

const OrganisationalDetails: React.FC<Props> = ({
  application,
  subjectTouchpoints,
  fetchSubjectTouchpoints,
  isLoadingTouchpoints,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState<boolean>(false)
  const [organisationSuccess, setOrganisationSuccess] = useState<string[]>([])
  const [organisationFail, setOrganisationFail] = useState<string[]>([])

  const corporateLetTenantSubject = isCorporateTenant(
    application.type,
    application?.context?.journeyType,
  )

  const outcomeSections = application?.outcome?.sections ?? []
  const organisationOutcome = outcomeSections.find(
    outcomeSection => outcomeSection.name === OutcomeSectionName.organisation,
  )
  const overallOutcomeStatus = application.outcome?.status
  const organisationOutcomeStatus = organisationOutcome?.status

  const sectionComments = subjectTouchpoints.filter(
    ({ category }) => category.toLowerCase() === 'organisation',
  )

  useEffect(() => {
    setOrganisationSuccess(
      displaySuccessReasons(application.results ?? [], 'organisation'),
    )
    setOrganisationFail(displayFailReasons(application.results ?? [], 'organisation'))
  }, [application])
  return (
    <>
      <CommentsSidebar
        disableLeaveComment={true}
        isOpen={commentsSidebarOpen}
        application={application}
        onClose={() => setCommentsSidebarOpen(false)}
        touchpoints={sectionComments}
        activeEntity="ORGANISATION"
        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
      />
      <ExpandableCard
        isExpanded={isExpanded}
        handleExpand={() => setIsExpanded(!isExpanded)}
        label="Organisation"
        icon="company"
        actionButton={
          <ActionButtonContainer>
            <div>
              <CommentsLabel
                isLoadingTouchpoints={isLoadingTouchpoints}
                onToggle={() => setCommentsSidebarOpen(!commentsSidebarOpen)}
                sectionComments={sectionComments}
              />
            </div>

            {overallOutcomeStatus && organisationOutcomeStatus && (
              <OutcomeStatusPill
                overallOutcomeStatus={overallOutcomeStatus}
                outcomeStatus={organisationOutcomeStatus}
              />
            )}
          </ActionButtonContainer>
        }
        expandedContent={
          <Body>
            <ButtonsContainer>
              <DocumentRequests
                customerId={application.customerId}
                applicationId={application.applicationId}
                subjectId={application.subjectId}
                sectionName={SectionName.organisation}
                subjectName={`${application.context?.firstName} ${application.context?.lastName}`}
                isRefCompleted={isRefCompleted(application)}
                isCorporateTenantSubject={corporateLetTenantSubject}
              />
              {application.context?.company && (
                <VerifyOrganisation
                  customerId={application.customerId}
                  subjectId={application.subjectId}
                  company={application.context?.company}
                />
              )}
            </ButtonsContainer>
            <ProsCons pros={organisationSuccess} cons={organisationFail} />
            {application.context?.company && (
              <OrganisationalDetailsCard company={application.context?.company} />
            )}
          </Body>
        }
      />
    </>
  )
}

export default OrganisationalDetails
